import { ChevronDownIcon } from 'lucide-react'
import type { ComponentType } from 'react'

// Accent por portal. Default 'purple' mantém o visual atual de todas as páginas.
// Cores canônicas por tipo de org (ver orgTypeColors em OrgSwitcher):
// end_customer=purple, cloud_provider=blue, reseller=emerald.
export type TabsAccent = 'purple' | 'blue' | 'emerald'

const TAB_ACCENT: Record<TabsAccent, { active: string; focus: string }> = {
  purple: {
    active: 'border-purple-500 text-purple-600',
    focus: 'focus:border-purple-500 focus:ring-purple-500',
  },
  blue: {
    active: 'border-blue-500 text-blue-600',
    focus: 'focus:border-blue-500 focus:ring-blue-500',
  },
  emerald: {
    active: 'border-emerald-500 text-emerald-600',
    focus: 'focus:border-emerald-500 focus:ring-emerald-500',
  },
}

export type ResponsiveTabItem<T extends string = string> = {
  id: T
  label: string
  icon?: ComponentType<{ className?: string }>
  badge?: string
  disabled?: boolean
}

type ResponsiveTabsProps<T extends string = string> = {
  tabs: ResponsiveTabItem<T>[]
  value: T
  onChange: (tabId: T) => void
  ariaLabel?: string
  className?: string
  accent?: TabsAccent
}

/**
 * Mobile-first tabs helper.
 * Usage:
 * - Mobile: compact select control.
 * - Desktop: horizontal tab buttons.
 */
export function ResponsiveTabs<T extends string = string>({
  tabs,
  value,
  onChange,
  ariaLabel = 'Tabs',
  className = '',
  accent = 'purple',
}: ResponsiveTabsProps<T>) {
  const accentClasses = TAB_ACCENT[accent]
  return (
    <div className={className}>
      <div className="relative md:hidden">
        <select
          value={value}
          onChange={(event) => onChange(event.target.value as T)}
          className={`w-full appearance-none rounded-lg border border-gray-300 bg-white px-3 py-2 pr-10 text-sm font-medium text-gray-700 focus:ring-2 focus:outline-none ${accentClasses.focus}`}
          aria-label={ariaLabel}
        >
          {tabs.map((tab) => (
            <option key={tab.id} value={tab.id} disabled={tab.disabled}>
              {tab.label}
            </option>
          ))}
        </select>
        <ChevronDownIcon className="pointer-events-none absolute top-1/2 right-3 h-4 w-4 -translate-y-1/2 text-gray-500" />
      </div>

      <div className="hidden md:block">
        <nav
          className="flex gap-2 overflow-x-auto border-b border-gray-200 pb-px"
          aria-label={ariaLabel}
        >
          {tabs.map((tab) => {
            const Icon = tab.icon
            const isActive = value === tab.id
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => onChange(tab.id)}
                disabled={tab.disabled}
                className={`inline-flex items-center gap-2 border-b-2 px-1 py-3 text-sm font-medium whitespace-nowrap transition-colors ${
                  isActive
                    ? accentClasses.active
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                } ${tab.disabled ? 'cursor-not-allowed opacity-50' : ''}`}
              >
                {Icon ? <Icon className="h-4 w-4" /> : null}
                <span>{tab.label}</span>
                {tab.badge ? (
                  <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                    {tab.badge}
                  </span>
                ) : null}
              </button>
            )
          })}
        </nav>
      </div>
    </div>
  )
}
